// web/src/lib/dates.js
// How a date reads. One place, so it reads the same way twice.
//
// American order throughout: September 9, 2027. Every call names 'en-US'
// explicitly rather than letting the browser decide — four dashboards were
// passing `undefined` as the locale, which renders "9 September" on a phone set
// to British English and "2027/9/9" on one set to Japanese. A barn in
// California should not see a different date because somebody's handset says
// so.
//
// Two functions, because there are two different things being formatted and
// conflating them is where off-by-one days come from:
//
//   fmtDate   a DATE — a due date, a shift, a birthday. No time, no zone. It
//             is the ninth of September wherever you are standing.
//   fmtStamp  an INSTANT — when a photo was uploaded, when a record was
//             archived. A real moment, shown in the reader's own zone.
//
// A date-only string is anchored at noon UTC and formatted in UTC. Parsing
// "2026-09-09" gives UTC midnight, and any viewer west of Greenwich formatting
// that in local time gets the 8th — the classic off-by-one that makes a due
// date land a day early for exactly the people it matters to.

const DATE_STYLES = {
  /** September 9, 2027 — the full form, when the year matters. */
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  /** Sep 9, 2027 */
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  /** Sep 9 — inside a list where the year is obvious. */
  short: { month: 'short', day: 'numeric' },
  /** Monday, September 9 — a heading for a day you are already looking at. */
  dayLong: { weekday: 'long', month: 'long', day: 'numeric' },
  /** Monday, Sep 9 — the same heading where the month has to be shorter. */
  dayMedium: { weekday: 'long', month: 'short', day: 'numeric' },
  /** Monday, Sep 9, 2026 */
  dayMediumYear: { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' },
  /** Mon, Sep 9 */
  dayShort: { weekday: 'short', month: 'short', day: 'numeric' },
  /** Mon, Sep 9, 2026 */
  dayShortYear: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
  /** Mon, 9/9 — a column too narrow for a month name. */
  dayNumeric: { weekday: 'short', month: 'numeric', day: 'numeric' },
  /** Mon */
  weekday: { weekday: 'short' },
  /** Monday — the heading of a planner page, which names the day you turned to. */
  weekdayLong: { weekday: 'long' },
  /** September 7 — the date under that heading, with no weekday repeating it. */
  monthDay: { month: 'long', day: 'numeric' },
  /** 9/9 — only where a column is too narrow for words. */
  numeric: { month: 'numeric', day: 'numeric' },
};

/**
 * The escape hatch: an ISO date with raw Intl options.
 *
 * The month calendar asks for `{ day: 'numeric' }` on its own — a bare "7" in a
 * grid cell — and a named style for that would be a style with no name. So one
 * caller keeps the freedom, and this is still the only place that decides the
 * locale and the noon-UTC anchoring. Prefer fmtDate: a style you can name is a
 * style two screens can share.
 */
export function fmtDateOpts(iso, opts = {}) {
  if (!iso || typeof iso !== 'string') return '';
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const d = new Date(day + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

/**
 * An ISO date (YYYY-MM-DD) as a person reads it.
 *
 * Returns '' for anything missing or unreadable rather than "Invalid Date",
 * which is worse than a blank: it looks like a bug in the record instead of an
 * absent value.
 */
export function fmtDate(iso, style = 'medium') {
  if (!iso || typeof iso !== 'string') return '';
  return fmtDateOpts(iso, DATE_STYLES[style] ?? DATE_STYLES.medium);
}

/**
 * A date with the year only when the year is not obvious.
 *
 * "Sep 9" is the right length for something happening this year and a trap for
 * something happening next one: a reorder date eleven months out rendered as
 * "Jul 31" reads as a date that has already passed. Inside `today`'s year this
 * is `short`; outside it, `medium`.
 *
 * `today` is the barn's day, handed in rather than taken from the clock — the
 * same rule as everywhere else in here, because the browser's year is not
 * necessarily the barn's on the 31st of December.
 */
export function fmtDateNear(iso, today, { near = 'short', far = 'medium' } = {}) {
  if (!iso || typeof iso !== 'string') return '';
  const sameYear = typeof today === 'string' && iso.slice(0, 4) === today.slice(0, 4);
  return fmtDate(iso, sameYear ? near : far);
}

/**
 * A unix timestamp (seconds) as a person reads it, in their own timezone.
 *
 * This one genuinely is a moment rather than a day, so the reader's zone is the
 * right zone: a photo uploaded at 11pm in California was uploaded on a
 * different date to somebody reading it in London, and both are correct.
 */
export function fmtStamp(unix, style = 'medium', withTime = false) {
  if (!unix && unix !== 0) return '';
  const d = new Date(unix * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const opts = DATE_STYLES[style] ?? DATE_STYLES.medium;
  return withTime
    ? d.toLocaleString('en-US', { ...opts, hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', opts);
}

/** Today, as a heading. Used before the server has said what the barn's today is. */
export function todayLong() {
  return new Date().toLocaleDateString('en-US', DATE_STYLES.dayLong);
}
